import { useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Shield } from 'lucide-react';
import { DSP_CRITERION } from '../../lib/roleLabels';
import { glassCard, bgStyle } from '../../lib/onboardingStyles';
import VerifyModal from '../common/VerifyModal';

// The verification offer for a new DSP, shown once after sign-up finishes
// (AppShell → finishSignup → here). It was onboarding's last step until email
// confirmation: with confirmation on, the account has no session until the
// member confirms, and every method needs one (badge upload path, queue RPC).
// VerifyModal is the one place the methods live; this screen only explains and
// opens it. Protected, outside AppShell, so it keeps onboarding's look.
const GetVerifiedScreen = () => {
  const navigate = useNavigate();
  // false, 'list' (all methods) or 'code' (straight to the code form)
  const [showVerifyModal, setShowVerifyModal] = useState(false);
  const verifySubmitted = useRef(false);

  return (
    <div className="min-h-screen flex flex-col" style={{ ...bgStyle, paddingTop: 'var(--sat)' }}>
      <div className="flex-1 flex flex-col px-6 pt-10 pb-6 max-w-lg mx-auto w-full">
        <div className="flex-1 flex flex-col justify-center splash-entrance">
          <div className="mb-6">
            <div
              className="w-12 h-12 mb-4 rounded-xl flex items-center justify-center"
              style={{
                background: 'linear-gradient(135deg, rgba(255,255,255,0.2), rgba(255,255,255,0.05))',
                border: '1px solid rgba(255,255,255,0.2)',
              }}
            >
              <Shield size={24} className="text-white" strokeWidth={1.5} />
            </div>
            <h2 className="text-2xl font-bold text-white mb-2">You're in. One last thing.</h2>
            <p className="text-sm" style={{ color: 'rgba(255,255,255,0.6)' }}>
              Get verified to join the conversation.
            </p>
          </div>

          <div className="rounded-2xl p-5 mb-6 space-y-3" style={glassCard}>
            <p className="font-semibold text-white">{DSP_CRITERION.headline}</p>
            <p className="text-sm text-white">{DSP_CRITERION.qualifies}</p>
            <p className="text-sm" style={{ color: 'rgba(255,255,255,0.7)' }}>{DSP_CRITERION.unlocks}</p>
            <p className="text-xs" style={{ color: 'rgba(255,255,255,0.55)' }}>{DSP_CRITERION.reviewed}</p>
          </div>

          <button
            onClick={() => setShowVerifyModal('list')}
            className="w-full py-3.5 rounded-xl font-semibold text-white text-base mb-3 transition-all duration-200 hover:brightness-110 active:scale-[0.98]"
            style={{ backgroundColor: 'var(--brand-primary)' }}
          >
            Verify now
          </button>
          <button
            onClick={() => setShowVerifyModal('code')}
            className="w-full py-2 mb-1 text-sm font-semibold underline transition-all active:scale-[0.98]"
            style={{ color: 'rgba(255,255,255,0.85)' }}
          >
            I have a DSP verification code
          </button>
          <button
            onClick={() => navigate('/feed', { replace: true })}
            className="w-full py-2 text-sm font-medium transition-all active:scale-[0.98]"
            style={{ color: 'rgba(255,255,255,0.6)' }}
          >
            Skip, I'll browse first
          </button>
          <p className="text-xs text-center mt-3" style={{ color: 'rgba(255,255,255,0.45)' }}>
            You can verify later from your profile.
          </p>

          {showVerifyModal && (
            <VerifyModal
              initialMethod={showVerifyModal === 'code' ? 'code' : null}
              onSubmitted={() => { verifySubmitted.current = true; }}
              onClose={() => {
                setShowVerifyModal(false);
                if (verifySubmitted.current) navigate('/feed', { replace: true });
              }}
            />
          )}
        </div>
      </div>
    </div>
  );
};

export default GetVerifiedScreen;
